import {MutableRefObject, useCallback, useEffect, useRef, useState} from 'react';
import AdminService from '@/modules/admin/services/adminService';
import {logError} from '@/shared/utils/logger';
import {BiomarkerSearchResult} from '../types';

export interface UseBiomarkerSearch {
    searchResults: BiomarkerSearchResult[];
    activeIndex: number | null;
    isSearching: boolean;
    dropdownRef: MutableRefObject<HTMLDivElement | null>;
    search: (term: string, index: number) => void;
    close: () => void;
}

export const useBiomarkerSearch = (): UseBiomarkerSearch => {
    const [searchResults, setSearchResults] = useState<BiomarkerSearchResult[]>([]);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const [isSearching, setIsSearching] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setSearchResults([]);
                setActiveIndex(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const search = useCallback((term: string, index: number) => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        if (term.trim().length < 2) {
            setSearchResults([]);
            setActiveIndex(null);
            setIsSearching(false);
            return;
        }

        setIsSearching(true);
        setActiveIndex(index);

        timeoutRef.current = setTimeout(async () => {
            try {
                const results = await AdminService.searchBiomarkers(term);
                setSearchResults(results);
            } catch (error) {
                logError('Failed to search biomarkers', error);
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        }, 300);
    }, []);

    const close = useCallback(() => {
        setSearchResults([]);
        setActiveIndex(null);
    }, []);

    return {searchResults, activeIndex, isSearching, dropdownRef, search, close};
};
